/**
 * VipProgramAdmin.tsx — Admin > Patients/Leads > V.I.P. Program.
 *
 * Legacy's V.I.P. Program page (admin_user_tags.php) for the signed-in
 * account: the medal ranges of its three V.I.P. tags — patients referred,
 * money spent in the practice, money spent by referrals. Each tag shows its
 * bronze, silver, gold and platinum medals with From / To (platinum has no
 * upper end: "and up"). The scheduler's patient pop-up shows the highest
 * medal a patient reaches.
 * Checks, as legacy's: whole numbers of 0 or more, From below To (these keep
 * Save off); a tier starting at or below the previous tier's end overlaps it
 * (a warning, as legacy only marked it). Save writes the changed tiers.
 * On a Locked page (useAdminPageLevel) the ranges are read-only values and
 * there is no Save or Revert.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { fieldClasses, type FieldState } from '@/components/admin/DialogChrome';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { MedalDisc } from '@/components/scheduling/MedalDisc';
import { useElementSize } from '@/hooks/useElementSize';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { isMoneyTag, listVipTags, saveVipTier, vipRangeIssues, type VipRangeIssue, type VipTag, type VipTier } from '@/services/vipProgramAdmin.service';
import { toast } from '@/store/toastStore';

type RangeForm = Record<string, string>;

const key = (tier: Pick<VipTier, 'id'>, field: 'min' | 'max') => `${tier.id}:${field}`;

function formFrom(tags: VipTag[]): RangeForm {
  const form: RangeForm = {};
  for (const tag of tags) {
    for (const tier of tag.tiers) {
      form[key(tier, 'min')] = String(tier.min);
      if (tier.name !== 'platinum') form[key(tier, 'max')] = tier.max === null ? '' : String(tier.max);
    }
  }
  return form;
}

/** A field as a number: null when empty; NaN when not a whole number. */
function parse(value: string | undefined): number | null {
  const v = (value ?? '').trim();
  if (v === '') return null;
  return /^-?\d+$/.test(v) ? Number(v) : Number.NaN;
}

const TAG_HINTS: Record<number, string> = {
  1: 'How many patients a patient has referred.',
  2: 'What a patient has spent in the practice.',
  3: 'What the patients they referred have spent.',
};

/** The narrowest a medal card reads well at: two From / To fields side by side. */
const TIER_CARD_MIN = 180;
const TIER_GAP = 12;

/**
 * A tag's four medals: in one row while each card gets TIER_CARD_MIN, then two
 * by two, then one under another. It answers to the section's own width, not
 * the window's: the app menu and the Admin rail take ~310px at any window
 * width (useElementSize.ts). Unmeasured (0) is the one-row layout.
 */
function TierGrid({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(ref);
  const fits = (cols: number) => width === 0 || width >= cols * TIER_CARD_MIN + (cols - 1) * TIER_GAP;
  const cols = fits(4) ? 'grid-cols-4' : fits(2) ? 'grid-cols-2' : 'grid-cols-1';
  return (
    <div ref={ref} className={cn('mt-4 grid gap-3', cols)}>
      {children}
    </div>
  );
}

export function VipProgramAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [tags, setTags] = useState<VipTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const t = useTrackedForm<RangeForm>(() => ({}));
  const { reset } = t;

  const load = useCallback(async () => {
    try {
      const list = await listVipTags();
      setTags(list);
      reset(formFrom(list));
      setFailed(false);
    } catch (err) {
      setFailed(true);
      toast.error(getErrorMessage(err, 'Failed to load the V.I.P. program'));
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Checks per tag, on the values as typed.
  const issues = useMemo(() => {
    const all: VipRangeIssue[] = [];
    for (const tag of tags) {
      const values = tag.tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        min: parse(t.form[key(tier, 'min')]),
        max: tier.name === 'platinum' ? null : parse(t.form[key(tier, 'max')]),
      }));
      for (const v of values) {
        if (Number.isNaN(v.min)) all.push({ tierId: v.id, field: 'min', level: 'error', message: 'Whole numbers only' });
        if (Number.isNaN(v.max)) all.push({ tierId: v.id, field: 'max', level: 'error', message: 'Whole numbers only' });
      }
      all.push(
        ...vipRangeIssues(
          values.map((v) => ({ ...v, min: Number.isNaN(v.min) ? null : v.min, max: Number.isNaN(v.max) ? null : v.max })),
        ).filter((i) => !all.some((a) => a.tierId === i.tierId && a.field === i.field)),
      );
    }
    return all;
  }, [tags, t.form]);
  const errorCount = issues.filter((i) => i.level === 'error').length;
  const issueFor = (tier: VipTier, field: 'min' | 'max') => issues.find((i) => i.tierId === tier.id && i.field === field) ?? null;

  async function save() {
    if (errorCount > 0) return;
    const changedTiers = tags.flatMap((tag) => tag.tiers).filter((tier) => t.changed.has(key(tier, 'min')) || t.changed.has(key(tier, 'max')));
    setSaving(true);
    try {
      for (const tier of changedTiers) {
        await saveVipTier(tier, parse(t.form[key(tier, 'min')]) ?? 0, tier.name === 'platinum' ? null : parse(t.form[key(tier, 'max')]));
      }
      toast.success('V.I.P. ranges saved');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The V.I.P. ranges could not be saved.'));
      await load();
    } finally {
      setSaving(false);
    }
  }

  const actions = (
    <div className="flex flex-wrap items-center gap-3">
      {t.dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!t.dirty || errorCount > 0 || loading || failed} loading={saving}>
        Save
      </Button>
    </div>
  );

  function rangeInput(tag: VipTag, tier: VipTier, field: 'min' | 'max') {
    const issue = issueFor(tier, field);
    const state: FieldState = issue?.level ?? 'none';
    const tierLabel = `${tier.name[0].toUpperCase()}${tier.name.slice(1)}`;
    return (
      <Input
        aria-label={`${tag.title} ${tierLabel} ${field === 'min' ? 'from' : 'to'}`}
        inputMode="numeric"
        value={t.form[key(tier, field)] ?? ''}
        leftAddon={isMoneyTag(tag) ? '$' : undefined}
        className={cn('h-8 px-2.5 py-1 text-sm tabular-nums', isMoneyTag(tag) && 'pl-6', fieldClasses.input(state))}
        onChange={(e) => t.patch({ [key(tier, field)]: e.target.value })}
      />
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader title="V.I.P. Program" subtitle="The ranges that earn patients their medals" actions={locked ? undefined : actions} />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading the V.I.P. program…" />
      ) : failed ? (
        <p className="py-16 text-center text-sm text-slate-500">The V.I.P. program could not be loaded. Reload the page to try again.</p>
      ) : tags.length === 0 ? (
        <p className="py-16 text-center text-sm text-slate-500">This account has no V.I.P. tags.</p>
      ) : (
        <AdminLockedFieldset>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pb-6">
            {tags.map((tag) => (
              <section key={tag.id} className="max-w-5xl rounded-lg border border-slate-200 bg-white p-5" data-testid={`vip-tag-${tag.typeId}`}>
                <h3 className="text-sm font-semibold text-slate-900">{tag.title}</h3>
                {TAG_HINTS[tag.typeId] && <p className="mt-0.5 text-xs text-slate-500">{TAG_HINTS[tag.typeId]}</p>}
                <TierGrid>
                  {tag.tiers.map((tier) => {
                    const tierLabel = `${tier.name[0].toUpperCase()}${tier.name.slice(1)}`;
                    const shown = issueFor(tier, 'min') ?? issueFor(tier, 'max');
                    const changed = t.changed.has(key(tier, 'min')) || t.changed.has(key(tier, 'max'));
                    return (
                      <div
                        key={tier.id}
                        className={cn('flex min-w-0 flex-col items-center gap-2 rounded-lg border px-3 pb-3 pt-4', changed ? 'border-blue-300 bg-blue-50/20' : 'border-slate-200')}
                      >
                        <MedalDisc tier={tier.name} size="lg" />
                        <span className="text-sm font-semibold text-slate-800">{tierLabel}</span>
                        <div className="mt-1 grid w-full max-w-[15rem] grid-cols-1 gap-2 sm:grid-cols-2">
                          <div className="min-w-0">
                            <span className="mb-1 block text-xs font-medium text-slate-500">From</span>
                            {rangeInput(tag, tier, 'min')}
                          </div>
                          <div className="min-w-0">
                            <span className="mb-1 block text-xs font-medium text-slate-500">To</span>
                            {tier.name === 'platinum' ? (
                              <span className="flex h-8 items-center rounded-md border border-dashed border-slate-200 px-2.5 text-sm text-slate-500">and up</span>
                            ) : (
                              rangeInput(tag, tier, 'max')
                            )}
                          </div>
                        </div>
                        {shown && <p className={cn('text-center', fieldClasses.message(shown.level))}>{shown.message}</p>}
                      </div>
                    );
                  })}
                </TierGrid>
              </section>
            ))}
          </div>
        </AdminLockedFieldset>
      )}
    </div>
  );
}
